import { useEffect, useRef } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import ClickableText from './ClickableText'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'

interface DefinitionBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  definition: string
  /** Optional Example field. When provided, the sheet renders both the full
   *  Definition and the full Example as separate sections — used so that long
   *  user-authored content (truncated in the card preview) is fully accessible. */
  example?: string
  speak: (text: string) => void
  isSpeechSupported: boolean
  /** Pixel height of the sheet. Parent reserves the same amount of space
   *  above so the sheet never overlaps the prompt card or the input. */
  height?: number
}

/**
 * Mobile-only bottom sheet that reveals the card Definition.
 *
 * Sits flush at bottom: 0 of the layout viewport. The caller is expected to
 * dismiss the soft keyboard (blur the focused input) BEFORE opening, so the
 * sheet doesn't render behind the IME. No bottomOffset / visualViewport
 * tracking is needed for this reason. On close the caller does NOT auto-
 * refocus the input — the user taps it themselves to resume typing.
 *
 * Closes via swipe-down, click outside, Escape, or programmatic close.
 */
export default function DefinitionBottomSheet({
  isOpen, onClose, definition, example, speak, isSpeechSupported, height,
}: DefinitionBottomSheetProps) {
  const { tr } = useLocale()
  const shouldReduceMotion = useReducedMotion()
  const sheetRef = useRef<HTMLDivElement>(null)
  // ADR-106 P3: blocks while open. Learn is off the swipe chain already; this
  // keeps the rule "every overlay joins" without exceptions.
  useSwipeNavBlock(isOpen, 'definition-bottom-sheet')

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const duration = shouldReduceMotion ? 0 : 0.22

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — tap outside to close. Transparent-ish so the prompt card
              behind remains visible. zIndex 90 sits above WordReferenceTrigger (default flow)
              and below the GrammarNoteCard interstitial (zIndex 85+). */}
          <motion.div
            key="def-sheet-backdrop"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0, 0, 0, 0.28)',
              zIndex: 90,
            }}
          />

          <motion.div
            key="def-sheet"
            ref={sheetRef}
            role="dialog"
            aria-label={tr.learn.definitionLabel}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose()
            }}
            // Don't initiate drag from the iOS edge-swipe-back zone (leftmost
            // 20px). Without this guard, framer-motion's pointerdown listener
            // can swallow the gesture before iOS recognizes it as edge swipe,
            // breaking the system back gesture inside the sheet. See ADR-013.
            onPointerDownCapture={(e) => {
              if (e.clientX <= 20) e.stopPropagation()
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', ease: [0.32, 0.72, 0, 1], duration }}
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              height: height != null ? height : undefined,
              maxHeight: height != null ? height : '60dvh',
              background: 'var(--bg-surface)',
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              boxShadow: 'var(--shadow-float)',
              borderTop: '1px solid var(--border)',
              zIndex: 91,
              display: 'flex', flexDirection: 'column',
              touchAction: 'pan-y',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Grab handle */}
            <div style={{
              display: 'flex', justifyContent: 'center', padding: '8px 0 4px',
              flexShrink: 0,
            }}>
              <div style={{
                width: 40, height: 4, borderRadius: 50,
                background: 'var(--border)',
              }} />
            </div>

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '4px 20px 10px', flexShrink: 0,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                {tr.learn.definitionLabel}
              </span>
              <button
                onClick={onClose}
                aria-label={tr.learn.closeDefinition}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', padding: 6, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 32, minHeight: 32,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Scrollable body — internal scroll so long definitions fit */}
            <div
              // Prevent drag from starting on content scroll — touchAction handles vertical pan
              onPointerDownCapture={(e) => e.stopPropagation()}
              style={{
                flex: 1, minHeight: 0,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                padding: '4px 20px 24px',
                fontSize: 16, fontFamily: 'var(--font-content)',
                color: 'var(--text-primary)', lineHeight: 1.6,
              }}
            >
              <ClickableText text={definition} speak={speak} isSpeechSupported={isSpeechSupported} />
              {example && (
                <>
                  <div style={{
                    marginTop: 20, paddingTop: 14,
                    borderTop: '1px solid var(--border)',
                    fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                    marginBottom: 8,
                  }}>
                    {tr.learn.exampleLabel}
                  </div>
                  <ClickableText text={example} speak={speak} isSpeechSupported={isSpeechSupported} />
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
